import { compose } from 'recompose';

import waitForGraphQL from 'js/lib/waitForGraphQL';

import type {
  GetBaseCertificateSlugsQuery as GetBaseCertificateSlugsQueryData,
  GetBaseCertificateSlugsQueryVariables,
} from 'bundles/enterprise-admin-grow-with-google/utils/__generated__/GetBaseCertificateSlugsQuery';
import { getCertificateTitle, googleCertificatesBase } from 'bundles/enterprise-admin-grow-with-google/utils/constants';
import { GetBaseCertificateSlugsQuery } from 'bundles/enterprise-admin-grow-with-google/utils/queries';

export type CertificateType = {
  productId: string;
  title: string;
  hasLanguage?: boolean;
  baseCertificateId?: string;
};

type PropsFromGetNewestCertificateIdsQuery = {
  slugMatcher: Record<string, string>;
};

export type PropsFromWithGoogleCertificates = {
  gwgCertificates: Record<string, CertificateType>;
  gwgLanguages: Record<string, CertificateType>;
};

export type PropsFromWithGoogleCertificatesOptional = {
  gwgCertificates?: Record<string, CertificateType>;
  gwgLanguages?: Record<string, CertificateType>;
};

export type PropsFromWithGoogleCertificatesLanguages = {
  gwgLanguages: CertificateType[];
};

export type PropsFromWithGoogleCertificatesOnly = {
  gwgCertificates: Record<string, CertificateType>;
};

// Currently, there is no clean way to retrieve both the base s12n id and newest s12n id in one call.
// First call to get the slug for base ids,
// and second call to get the newest ids and match via slug from first call.
const withGoogleCertificates = () => {
  return compose(
    waitForGraphQL<
      {},
      GetBaseCertificateSlugsQueryData,
      GetBaseCertificateSlugsQueryVariables,
      PropsFromGetNewestCertificateIdsQuery
    >(GetBaseCertificateSlugsQuery, {
      options: () => ({
        variables: {
          s12nIds: Object.keys(googleCertificatesBase),
          limit: Object.keys(googleCertificatesBase).length,
        },
      }),
      props: ({ data }) => {
        const slugMatcher = {} as Record<string, string>;
        data?.OnDemandSpecializationsV1Resource?.latestInFamiliesWithBaseIdKey?.elements?.forEach((e) => {
          if (e?.slug && e?.id) {
            slugMatcher[e.slug] = e.id;
          }
        });

        return {
          slugMatcher,
        };
      },
    }),
    waitForGraphQL<
      PropsFromGetNewestCertificateIdsQuery,
      GetBaseCertificateSlugsQueryData,
      GetBaseCertificateSlugsQueryVariables,
      PropsFromWithGoogleCertificates
    >(GetBaseCertificateSlugsQuery, {
      options: () => ({
        variables: {
          s12nIds: Object.keys(googleCertificatesBase),
          limit: Object.keys(googleCertificatesBase).length,
        },
      }),
      props: ({ data, ownProps }) => {
        const gwgCertificates = {} as Record<string, CertificateType>;
        const gwgLanguages = {} as Record<string, CertificateType>;

        data?.OnDemandSpecializationsV1Resource?.latestInFamiliesWithBaseIdKey?.elements?.forEach((c) => {
          if (c?.slug && c?.id && c?.launchedAt) {
            const originalCertificate = googleCertificatesBase[ownProps.slugMatcher[c.slug]] as {
              title: string;
              hasLanguage?: boolean;
              baseCertificateId?: string;
            };
            const dictToFill = originalCertificate.baseCertificateId ? gwgLanguages : gwgCertificates;

            dictToFill[originalCertificate.title] = {
              baseCertificateId: originalCertificate.baseCertificateId,
              title: getCertificateTitle(originalCertificate.title),
              hasLanguage: originalCertificate.hasLanguage,
              productId: c.id,
            };
          }
        });

        return {
          gwgCertificates,
          gwgLanguages,
        };
      },
    })
  );
};

export default withGoogleCertificates;
